import { z } from "zod/v4"
import { eventTypeSchema } from "@/schemas/shared"
import { promoBannerRedirectOption } from "@/constants/websiteInfoConst"

export const filterContextSchema = z.object({
  buttonText: z.string(),
  sortIndex: z.number(),
  eventType: eventTypeSchema.optional(),
  eventId: z.number().optional(),
  marketId: z.string().optional(),
  isExchangeTicket: z.boolean().optional()
})

export const supportInfoItemSchema = z.object({
  displayName: z.string(),
  value: z.string(),
  priority: z.number()
})

export const queryInItInfoResponseSchema = z.object({
  JblName: z.string(),
  // null when the apiLogoutUrl cookie is absent
  apiLogoutUrl: z.string().nullable(),
  // null when the site has no player sign-up setting
  bannerSettingUrl: z.string().nullable(),
  betfairCurrencyMaxOdds: z.number(),
  betfairCurrencyMinOdds: z.number(),
  bettingOddsLength: z.number(),
  bettingStakeLength: z.number(),
  coinPreference: z.string(),
  // null when the IP-to-country lookup fails to initialise
  country: z.string().nullable(),
  currencySymbol: z.string(),
  customizeStake: z.string().nullable(),
  dC: z.array(z.string()),
  enableAsianhandicapForecastDesc: z.boolean(), // For EnterFair
  enableCustomizeStake: z.boolean(),
  enableExpandHightLightView: z.boolean(),
  enableGliveStreaming: z.boolean(),
  enableH2HButton: z.boolean(),
  enableJblCollectPlayerInfo: z.boolean(),
  enableJblIntelligentChangeLine: z.boolean(),
  enableJblWebsocketListening: z.boolean(),
  enableLiveScoreMatchTracker: z.boolean(),
  enableLiveScoreMatchTrackerExpanded: z.boolean(),
  enableLoginChangeLine: z.boolean(),
  enableMarketLiquidityCache: z.boolean(),
  enableMobileHightlightCompetitionView: z.boolean(),
  enableNeuaAnalytics: z.boolean(),
  enableNonIntegerStake: z.boolean(),
  enableNoTxnIdle: z.boolean(),
  enableOcerNavigation: z.boolean(),
  enableQueryDataSubdomain: z.boolean(),
  enableRacingWebsiteGreyhoundPlayLay: z.boolean(),
  enableRacingWebsiteHorsePlayLay: z.boolean(),
  enableReduceBetfairSizeFromFrontend: z.boolean(),
  enableShowSportradarSportsbookForecast: z.boolean(),
  enableShowGeniusSportsForecast: z.boolean(),
  enableStreamingAkamai: z.boolean(),
  enableStreamingAli: z.boolean(),
  enableStreamingLimelight: z.boolean(),
  enableStreamingPlayListV2: z.boolean(),
  enableStreamingTencent: z.boolean(),
  enableStreamingWangsu: z.boolean(),
  enableStreamingEdgecast: z.boolean(),
  enableStreamingCDNetwork: z.boolean(),
  eventLabel: z.array(filterContextSchema),
  fancybetKhaddaMaxRuns: z.number(),
  fancybetKhaddaMinRuns: z.number(),
  fancybetKhaddaRunsMaxLength: z.number(),
  getFancyBetMarketsTaskCycleTime: z.number().nullable(),
  groupSite: z.number(),
  hasAccount: z.boolean(),
  isApp: z.boolean(),
  isFake: z.boolean(),
  isCheckBookMode: z.boolean(),
  isDemoBtnShow: z.boolean(),
  isEnableAcceptAnyOdds: z.boolean(),
  isEnableBookMaker: z.boolean(),
  isEnableElectionFancyBet: z.boolean(),
  isEnableFancyBet: z.boolean(),
  isEnableGameHall: z.boolean(),
  isEnableLowLiquidity: z.boolean(),
  isEnableMiniGameEntrance: z.boolean(),
  isEnableMobileWebSocket: z.boolean(),
  isEnableMobileReportPage: z.boolean(),
  isEnablePlayerNeedLogin: z.boolean(),
  isEnableSportsBookBet: z.boolean(),
  isEnableGeniusSportsBet: z.boolean(),
  isEnableYuviEntrance: z.boolean(),
  isEnterFairGroup: z.boolean(), // 暫時不處理EnterFair
  isFancyFairGroup: z.boolean(),
  isFromApi: z.boolean(),
  isFromWeb: z.boolean(),
  isHideUpcomingHorseRace: z.boolean(),
  isShowBetfairIcon: z.boolean(),
  isMobileAppActive: z.boolean(),
  isMobileNavigation: z.boolean(),
  isMobileOrTablet: z.boolean(),
  isOfferGeniusSports: z.boolean(),
  isOfferSportradarSportsBook: z.boolean(),
  isPromotionPlaying: z.boolean(),
  isRacingWebSite: z.boolean(),
  isShowPromoBanner: z.boolean(),
  isShowVirtualCricket: z.boolean(),
  isSkyInplayGroup: z.boolean(),
  isTempRole: z.boolean(),
  isUseWebSocket: z.boolean(),
  isValidationCodeEnable: z.boolean(),
  isViewedAnnouncement: z.boolean(),
  jblChannelId: z.string(),
  jblHashCode: z.string(),
  lowLiquidityForMatchOdds: z.number(),
  lowLiquidityForOthers: z.number(),
  marketLiquiditys: z.record(z.string(), z.number()),
  minBet: z.number(),
  mobileDownloadUrl: z.string(),
  mobileVersion: z.string(),
  mobileSize: z.string(),
  mobileData: z.string(),
  oddsMinBetMap: z.record(z.string(), z.number()),
  playerCurrency: z.number(),
  playerCurrencyType: z.string(),
  // null when the site has no popup message configured
  popupAnnouncementKeys: z
    .object({
      desktopImgUrl: z.string(),
      desktopMessage: z.string(),
      mobileImgUrl: z.string(),
      mobileMessage: z.string()
    })
    .nullable(),
  promoBanner: z.array(
    z.object({
      fileName: z.string(),
      redirectOption: z.enum(promoBannerRedirectOption),
      url: z.string()
    })
  ),
  qD: z.array(z.string()),
  queryPass: z.string(),
  redirectDomain: z.string().optional(),
  soccerMenuOrderMap: z.array(z.record(z.string(), z.number())),
  // Left out for API white-labels; each channel is present only when the site has rows for it.
  supportInfo: z
    .object({
      customer: z.array(supportInfoItemSchema).optional(),
      email: z.array(supportInfoItemSchema).optional(),
      facebook: z.array(supportInfoItemSchema).optional(),
      instagram: z.array(supportInfoItemSchema).optional(),
      telegram: z.array(supportInfoItemSchema).optional(),
      twitter: z.array(supportInfoItemSchema).optional(),
      whatsapp: z.array(supportInfoItemSchema).optional()
    })
    .optional(),
  taxRatio: z.number(),
  tempCtrlQueryDataOneSeconds: z.boolean(),
  tempCtrlUseWebsocketBetfair: z.boolean(),
  tempCtrlUseWebsocketFancybet: z.boolean(),
  tempCtrlUseWebsocketBookmaker: z.boolean(),
  userCoin: z.number(),
  userID: z.string(),
  valid: z.string(),
  webSiteCssName: z.string(),
  webSiteDescription: z.string(),
  webSiteDisplayName: z.string(),
  webSiteGroupId: z.number(),
  webSiteType: z.number(),
  timeZone: z.string()
})

export const queryEventLabelResponseSchema = z.object({
  eventLabel: z.array(filterContextSchema),
  status: z.string(),
  status_msg: z.string()
})
